import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const parseStateZip = (value) => {
  const text = String(value || "").trim();
  const match = text.match(/^([A-Za-z]{1,2})(?:\s+([\d-]+))?$/);
  if (!match) return { state: text.slice(0, 2).toUpperCase(), zip: "" };
  return { state: match[1].toUpperCase(), zip: match[2] || "" };
};

export const parseAddress = (value = "") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return { street: "", unit: "", city: "", state: "", zip: "" };

  const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) {
    const { state, zip } = parseStateZip(parts[parts.length - 1]);
    return {
      street: parts[0] || "",
      unit: parts.length > 3 ? parts.slice(1, -2).join(", ") : "",
      city: parts[parts.length - 2] || "",
      state,
      zip,
    };
  }

  const inlineMatch = text.match(/^(.*?)\s+([A-Za-z][A-Za-z .'-]{2,})\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (inlineMatch) {
    return {
      street: inlineMatch[1].trim(),
      unit: "",
      city: inlineMatch[2].trim(),
      state: inlineMatch[3].toUpperCase(),
      zip: inlineMatch[4],
    };
  }

  return { street: text, unit: "", city: "", state: "", zip: "" };
};

export const formatAddress = ({ street, unit, city, state, zip }) => {
  const streetParts = [street, unit].map((part) => String(part || "").trim()).filter(Boolean);
  const cityPart = String(city || "").trim();
  const statePart = String(state || "").trim().toUpperCase();
  const zipPart = String(zip || "").trim();
  const stateZip = [statePart, zipPart].filter(Boolean).join(" ");

  return [...streetParts, cityPart, stateZip].filter(Boolean).join(", ");
};

export const AddressFields = ({
  value = "",
  onChange,
  label = "Address",
  cityHint = "",
  testIdPrefix = "address",
  error = false,
  className = "",
}) => {
  const lastEmittedValue = useRef("");
  const [address, setAddress] = useState(() => parseAddress(value));

  useEffect(() => {
    if (value === lastEmittedValue.current) return;
    setAddress(parseAddress(value));
  }, [value]);

  const setAddressField = (field, nextValue) => {
    const nextAddress = {
      ...address,
      [field]: field === "state" ? nextValue.toUpperCase().slice(0, 2) : nextValue,
    };
    setAddress(nextAddress);
    const formattedAddress = formatAddress(nextAddress);
    lastEmittedValue.current = formattedAddress;
    onChange(formattedAddress);
  };

  return (
    <div className={className}>
      {label && <Label className="text-slate-700 font-medium">{label}</Label>}
      {error && (
        <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
          Please enter a full address with street, city, state, and ZIP.
        </p>
      )}
      <div className="mt-1 grid grid-cols-1 gap-3">
        <Input
          value={address.street}
          onChange={(event) => setAddressField("street", event.target.value)}
          placeholder="Street address"
          className={`h-11 border-slate-200 ${error ? "border-red-300 focus-visible:ring-red-200" : ""}`}
          data-testid={`${testIdPrefix}-street`}
        />
        <Input
          value={address.unit}
          onChange={(event) => setAddressField("unit", event.target.value)}
          placeholder="Suite, booth, unit, or landmark (optional)"
          className={`h-11 border-slate-200 ${error ? "border-red-300 focus-visible:ring-red-200" : ""}`}
          data-testid={`${testIdPrefix}-unit`}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_84px_120px]">
          <Input
            value={address.city}
            onChange={(event) => setAddressField("city", event.target.value)}
            placeholder={cityHint || "City"}
            className={`h-11 border-slate-200 ${error ? "border-red-300 focus-visible:ring-red-200" : ""}`}
            data-testid={`${testIdPrefix}-city`}
          />
          <Input
            value={address.state}
            onChange={(event) => setAddressField("state", event.target.value)}
            placeholder="OH"
            className={`h-11 border-slate-200 uppercase ${error ? "border-red-300 focus-visible:ring-red-200" : ""}`}
            data-testid={`${testIdPrefix}-state`}
          />
          <Input
            value={address.zip}
            onChange={(event) => setAddressField("zip", event.target.value)}
            placeholder="43215"
            className={`h-11 border-slate-200 ${error ? "border-red-300 focus-visible:ring-red-200" : ""}`}
            data-testid={`${testIdPrefix}-zip`}
          />
        </div>
      </div>
    </div>
  );
};
